import { useLayoutEffect, useState } from 'react';

import { InjectedFieldProps, PublicFieldProps } from 'react-admin';
import { DateTime } from 'luxon';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import {
  BumpResourceSheetDto,
  JobPhaseBumpApprovalStatus,
  RepairTypeEnum,
} from '@vatos-pas/common';

import { Show } from 'components/Show';
import {
  CAN_APPROVE_BUMPOUT,
  CAN_LOG_HOURS_BUMPOUT,
  CAN_MANAGE_HO_SERVICE_REPAIR,
} from 'providers/permissions';
import {
  canEditHOServiceRepair,
  isConfirmOnlyRepair,
  isPendingSupervisorStatus,
} from '../utils';
import { isApprovedStatus } from 'utils/bumpApproval';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { AppRole } from 'providers/roles';
import { BumpoutTimesheetFieldType } from '../views/bumpout-timesheet-list';
import ConfirmLogModal from './ConfirmLogModal';
import TimeLogModal, { TimeLogModalMode } from './TimeLogModal';

type LogButtonProps = {
  onChangeField: (
    id: string,
    name: BumpoutTimesheetFieldType,
    value: DateTime | string | null,
  ) => void;
  handleRefetch: () => void;
  loaded: boolean;
} & PublicFieldProps &
  InjectedFieldProps<BumpResourceSheetDto>;

const LogButton = ({
  record,
  onChangeField,
  handleRefetch,
  loaded,
}: LogButtonProps) => {
  const classes = useStyles();
  const { hasPermission, hasRole } = PermissionsProvider.useContainer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const isPiecework = record?.repairType === RepairTypeEnum.Piecework;
  const isConfirmOnly = isConfirmOnlyRepair(
    record?.repairType,
    record?.estimatedHours,
  );

  const canLogHours = hasPermission(CAN_LOG_HOURS_BUMPOUT);
  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);
  const isRegionalManager = hasRole(AppRole.RegionalManager);
  const isLaborCoordinator = hasRole(AppRole.LaborCoordinator);

  const isNotStarted =
    record?.bumperApprovalStatus === JobPhaseBumpApprovalStatus.NOT_STARTED;
  const isPendingSupervisorAction = isPendingSupervisorStatus(
    record?.bumperApprovalStatus,
  );

  // Initialize fields from record on mount (migrated from TimeInput)
  useLayoutEffect(() => {
    if (!loaded || !record?.id) return;

    const shouldInitialize =
      (isNotStarted || isPendingSupervisorAction) && canLogHours;

    if (shouldInitialize) {
      window.setTimeout(() => {
        if (record?.timeIn) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.TimeIn,
            DateTime.fromISO(record.timeIn as string),
          );
        }
        if (record?.timeOut) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.TimeOut,
            DateTime.fromISO(record.timeOut as string),
          );
        }
      }, 500);
    }
  }, [loaded, record?.id]);

  if (!record?.id) return null;

  const isApproved =
    isApprovedStatus(record?.bumperApprovalStatus) ||
    record?.bumperApprovalStatus === JobPhaseBumpApprovalStatus.DEPRECATED;

  const hasLoggedTime = Boolean(record?.timeIn && record?.timeOut);
  const hasLoggedHours = record?.loggedHours !== null;

  const getButtonConfig = (): {
    visible: boolean;
    label: string;
    mode: TimeLogModalMode;
  } | null => {
    if (isRegionalManager || isLaborCoordinator) {
      return null;
    }

    if (isApproved) {
      return null;
    }

    // NOT_STARTED status
    if (isNotStarted) {
      // Bumper or Admin can log/edit
      if (canLogHours) {
        if (isConfirmOnly || !hasLoggedTime) {
          return { visible: true, label: 'LOG', mode: 'log' };
        }
        return { visible: true, label: 'EDIT', mode: 'edit' };
      }
      return null;
    }

    // PENDING_APPROVAL or PENDING_REVIEW
    if (isPendingSupervisorAction) {
      // Hide if no logged hours
      if (!hasLoggedHours && !hasLoggedTime) {
        return null;
      }

      // Bumper can edit until supervisor action
      if (canLogHours) {
        return { visible: true, label: 'EDIT', mode: 'edit' };
      }

      if (
        canApprove &&
        canEditHOServiceRepair(record?.repairType, canManageHOService)
      ) {
        return { visible: true, label: 'EDIT', mode: 'edit' };
      }
    }

    return null;
  };

  const buttonConfig = getButtonConfig();

  if (!buttonConfig?.visible) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        className={classes.button}
        onClick={() =>
          isConfirmOnly ? setIsConfirmModalOpen(true) : setIsModalOpen(true)
        }
      >
        {buttonConfig.label}
      </Button>
      <Show condition={isConfirmModalOpen}>
        <ConfirmLogModal
          open={isConfirmModalOpen}
          handleClose={() => setIsConfirmModalOpen(false)}
          record={record}
          handleRefetch={handleRefetch}
          title={isPiecework ? 'Log - Piecework' : 'Log - Special Jobs'}
          confirmLabel={isPiecework ? 'CONFIRM' : 'LOG'}
          successMessage={
            isPiecework ? 'Piecework logged.' : 'Special job logged.'
          }
          emphasizeMessage={!isPiecework}
        />
      </Show>
      <Show condition={isModalOpen}>
        <TimeLogModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          record={record}
          mode={buttonConfig.mode}
          onChangeField={onChangeField}
          handleRefetch={handleRefetch}
        />
      </Show>
    </>
  );
};

const useStyles = makeStyles({
  button: {
    whiteSpace: 'nowrap',
    minWidth: 60,
  },
});

export default LogButton;
